import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { DailyQueueTab } from "@/features/daily/components/diagnostic/diagnostic-view-model";

export type QueueMenuItemVm = {
    kind: DailyQueueTab;
    label: string;
    href: string;
    active: boolean;
};

type Props = {
    items: QueueMenuItemVm[];
    /** «Más colas» on the day view; the current queue's name anywhere else. */
    label: string;
};

const MENU_ID = "daily-queue-menu";

/**
 * FR-018 + D131: the queues behind one menu button (WAI-ARIA menu pattern: arrows,
 * Home/End, Escape back to the button). Every item is still a real link, so an
 * incident or stage link keeps landing on its own queue.
 */
export const QueueMenu: React.FC<Props> = ({ items, label }) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

    const focusItem = (index: number): void => {
        const target = itemRefs.current[(index + items.length) % items.length];
        if (target !== null && target !== undefined) target.focus();
    };

    useEffect(() => {
        const first = itemRefs.current[0];
        if (open && first !== null && first !== undefined) first.focus();
    }, [open]);

    const close = (): void => {
        setOpen(false);
        if (buttonRef.current !== null) buttonRef.current.focus();
    };

    const onMenuKey = (event: React.KeyboardEvent<HTMLUListElement>): void => {
        const current = itemRefs.current.findIndex((item) => item === document.activeElement);
        if (event.key === "ArrowDown") { event.preventDefault(); focusItem(current + 1); }
        if (event.key === "ArrowUp") { event.preventDefault(); focusItem(current - 1); }
        if (event.key === "Home") { event.preventDefault(); focusItem(0); }
        if (event.key === "End") { event.preventDefault(); focusItem(items.length - 1); }
        if (event.key === "Escape") { event.preventDefault(); close(); }
        if (event.key === "Tab") setOpen(false);
    };

    return (
        <div
            className="daily-menu"
            onBlur={(event) => {
                if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
            }}
        >
            <button
                ref={buttonRef}
                type="button"
                className="daily-button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? MENU_ID : undefined}
                onClick={() => setOpen(!open)}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); }
                }}
            >
                {label} <DailyIcon name="expand_more" />
            </button>
            {!open ? null : (
                <ul className="daily-menu__list" role="menu" id={MENU_ID} aria-label="Colas operativas" onKeyDown={onMenuKey}>
                    {items.map((item, index) => (
                        <li key={item.kind} role="none">
                            <Link
                                ref={(node) => { itemRefs.current[index] = node; }}
                                role="menuitem"
                                tabIndex={-1}
                                className="daily-menu__item"
                                to={item.href}
                                aria-current={item.active ? "page" : undefined}
                                onClick={() => setOpen(false)}
                            >
                                {item.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
